import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  format,
  addDays,
  startOfWeek,
  eachDayOfInterval,
  isSameDay,
  isWeekend as dateFnsIsWeekend,
} from 'date-fns';
import { isUkBankHoliday } from '../../lib/ukBankHolidays';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar as CalendarIcon,
  Plus,
  RefreshCw,
  Inbox,
  X as XIcon,
  MapPin,
  AlertTriangle,
  SlidersHorizontal,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import clsx from 'clsx';
import {
  getJobsForDay,
  getDayPosition,
  getDayNumber,
  assignJobRows,
  CARD_HEIGHT,
} from '../../lib/boardUtils';
import { useJobs } from '../../hooks/useJobs';
import { useStaff } from '../../hooks/useStaff';
import { useBoardWeeks } from '../../hooks/useBoardWeeks';
import { useWorkItems } from '../../hooks/useWorkItems';
import { useConsecutiveDaysByDate } from '../../hooks/useConsecutiveDays';
import { useVehicles } from '../../hooks/useVehicles';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { JobCard } from './JobCard';
import { PhaseDragLayer } from './reschedule/PhaseDragLayer';
import { DragAutoScroller } from './reschedule/DragAutoScroller';
import { ReassignmentDialog } from './reschedule/ReassignmentDialog';
import { useRescheduleFlow } from '../../hooks/useRescheduleFlow';
import { PHASE_DRAG_TYPE, type PhaseDragItem } from '../../lib/dndTypes';
import { usePermissions } from '../../hooks/usePermissions';
import { JobDetailPanel } from './JobDetailPanel';
import { ScheduleModal } from './ScheduleModal';
import { BoardFilterBar, EMPTY_FILTERS, applyBoardFilters } from './BoardFilterBar';
import type { BoardFilters } from './BoardFilterBar';
import type { ApiWorkItem } from '../../lib/api';
import type { ApiStaff, ApiBoardLeaveEntry } from '../../lib/api';
import { adaptBoardPhaseRow, adaptPostponement } from '../../lib/adapters';
import type { Staff, Job } from '../data/mockData';
import {
  WINDOW_WEEKS,
  buildWindow,
  slideLeft,
  slideRight,
  scrollDeltaForSlide,
} from '../../lib/scheduleWindow';

const COL_WIDTH = 180;
const COL_WIDTH_MOBILE = 140;
// Must match the flex `gap-3` (0.75rem = 12px) on the day-columns container.
// One week's full horizontal advance = 7 columns + their 7 leading gaps.
const COL_GAP = 12;
const WEEK_PX = 7 * (COL_WIDTH + COL_GAP);

interface StaffChipData {
  staff: ApiStaff;
  /** available = not assigned, not on leave; assigned = has job that day; leave = on leave */
  status: 'available' | 'assigned' | 'leave';
}

/**
 * Return ALL active field-operative staff for a given day, tagged with their status.
 * Sorted available-first within each group so available chips lead the row.
 * Uses staff_role (operative/manager) for REP/MGR rows.
 *
 * GH #117: SUB (subcontractor) row removed from the availability strip.
 * The `sub` field is no longer returned; only `rep` and `other` (MGR) are present.
 *
 * Phase 26-03: filter `jobs` by `j.status IN ('scheduled', 'in_progress')` BEFORE
 * aggregating staffAssigned. The board's `jobs` array intentionally includes
 * cancelled / completed / archived / postponed jobs (per Phase 23-05 dual-read-
 * paths design) so the calendar can render tombstones + locked panels. But for
 * the day-column header staff-availability aggregation, only scheduled/in_progress
 * jobs count — cancelled jobs should NOT mark a worker as "assigned" for that day.
 * Mirrors the backend both-filters convention (Phase 23-05/06/07) at the
 * aggregation layer.
 */
function getAllStaffByDay(
  date: Date,
  staff: ApiStaff[],
  leave: ApiBoardLeaveEntry[],
  jobs: import('../data/mockData').Job[],
): { rep: StaffChipData[]; other: StaffChipData[] } {
  const dateStr = format(date, 'yyyy-MM-dd');
  const staffOnLeave = new Set(leave.filter((l) => l.date === dateStr).map((l) => l.staff_id));
  const staffAssigned = new Set(
    jobs
      .filter((j) => j.status === 'scheduled' || j.status === 'in_progress')
      .flatMap((j) => j.assignedStaff)
      .filter((a) => a.date === dateStr)
      .map((a) => a.staffId),
  );

  const toChip = (s: ApiStaff): StaffChipData => ({
    staff: s,
    status: staffOnLeave.has(s.id) ? 'leave' : staffAssigned.has(s.id) ? 'assigned' : 'available',
  });

  const sortChips = (chips: StaffChipData[]) =>
    [...chips].sort((a, b) => {
      const order = { available: 0, assigned: 1, leave: 2 };
      return order[a.status] - order[b.status];
    });

  // boardData.staff is already filtered to active field operatives server-side
  return {
    rep: sortChips(
      staff.filter((s) => !s.is_subcontractor && s.staff_role === 'operative').map(toChip),
    ),
    other: sortChips(
      staff.filter((s) => !s.is_subcontractor && s.staff_role === 'manager').map(toChip),
    ),
  };
}

/**
 * #217: a single day column, extracted so its `useDrop` hook can run per column
 * legally (hooks can't run inside `visibleDays.map`). The body is the SAME JSX the
 * board rendered inline before — every value it used is now threaded in as a prop,
 * so rendering is identical, plus a drop target that hands a dropped phase to the
 * board's reschedule flow.
 */
export function DayColumn({
  day,
  dayStaff,
  rowSlots,
  rowCount,
  isWeekLoading,
  consecutiveDaysMap,
  staffMap,
  vehiclesMap,
  canScheduleWork,
  canReschedule,
  reschedule,
  onAddJob,
  onSelectJob,
}: {
  day: Date;
  dayStaff: { rep: StaffChipData[]; other: StaffChipData[] };
  rowSlots: (Job | null)[];
  rowCount: number;
  isWeekLoading: boolean;
  consecutiveDaysMap: Record<string, number>;
  staffMap: Map<string, Staff>;
  vehiclesMap: Map<string, string[]>;
  canScheduleWork: boolean;
  canReschedule: boolean;
  reschedule: ReturnType<typeof useRescheduleFlow>;
  onAddJob: (dayStr: string) => void;
  onSelectJob: (job: Job, day: Date, section: 'vehicles' | 'staff' | null) => void;
}) {
  const dayStr = format(day, 'yyyy-MM-dd');
  const isWeekendDay = dateFnsIsWeekend(day);
  const isBankHoliday = isUkBankHoliday(dayStr);
  const isNonWorkingDay = isWeekendDay || isBankHoliday;
  const isToday = isSameDay(day, new Date());

  // #217: drop target. Confirm-first — the drop only opens the reschedule dialog;
  // the card is not moved until the user confirms (so cancel needs no rollback).
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: PHASE_DRAG_TYPE,
      canDrop: (item: PhaseDragItem) => item.startDate !== dayStr,
      drop: (item: PhaseDragItem) => {
        if (item.startDate === dayStr) return; // same-date drop = no-op
        const newEnd = format(addDays(day, Math.max(1, item.durationDays) - 1), 'yyyy-MM-dd');
        reschedule.startReschedule({
          jobId: item.jobId,
          phaseId: item.phaseId,
          newStartDate: dayStr,
          newEndDate: newEnd,
          fromLabel: `${item.ion ?? ''} ${item.name}`.trim(),
          isPhase: true,
        });
      },
      collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),
    }),
    [dayStr, day, reschedule],
  );

  return (
    <div
      data-day-col
      data-date={dayStr}
      // react-dnd v16 + React 19: the connector returns ReactElement|null, not a
      // valid ref-callback return — wrap in a void-returning callback ref.
      ref={(node) => {
        drop(node);
      }}
      className={clsx(
        'shrink-0 flex flex-col bg-white rounded-lg shadow-sm border overflow-hidden',
        isToday ? 'border-[#F26522] ring-1 ring-[#F26522]/20' : 'border-neutral-200',
        // single-column hovered affordance only (no cross-column span highlight)
        isOver && canDrop && 'ring-2 ring-[#F26522] bg-[#F26522]/5',
      )}
      style={{ width: COL_WIDTH, minWidth: COL_WIDTH_MOBILE }}
    >
      {/* Day Header */}
      <div
        className={clsx(
          'px-3 py-2 border-b flex items-center justify-between',
          isNonWorkingDay
            ? `bg-[#ccf5ff] ${isToday ? 'border-[#F26522]/20' : 'border-neutral-100'}`
            : isToday
              ? 'bg-[#F26522]/5 border-[#F26522]/20'
              : 'bg-neutral-50 border-neutral-100',
        )}
      >
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
            {format(day, 'EEE')}
          </div>
          <div className={clsx('text-[15px]', isToday ? 'text-[#F26522]' : 'text-neutral-900')}>
            {format(day, 'd MMM')}
          </div>
        </div>
        {canScheduleWork && (
          <button
            type="button"
            onClick={() => {
              // #100: open Available Work and seed this day as the start date.
              if (canScheduleWork) onAddJob(dayStr);
            }}
            className="p-1 rounded hover:bg-white text-neutral-400 hover:text-[#F26522] transition-colors"
            title="Add a job starting on this day"
            aria-label={`Add a job starting on ${format(day, 'd MMM yyyy')}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Staff availability — REP (operatives) only; SUB row removed (GH #117) */}
      <TooltipProvider>
        <div className="px-2 py-1.5 border-b border-neutral-100 space-y-1">
          {(
            [
              {
                key: 'rep',
                label: 'REP',
                chips: dayStaff.rep,
                color: 'bg-violet-500',
              },
            ] as const
          ).map(({ key, label, chips }) => (
            <div key={key} className="flex items-start pt-0.5 gap-1 h-[42px] overflow-hidden">
              <span className="text-[9px] text-neutral-400 w-6 shrink-0 font-medium">{label}</span>
              <div className="flex flex-wrap gap-0.5">
                {isWeekLoading ? (
                  <div className="h-4 w-12 bg-neutral-100 rounded animate-pulse" />
                ) : chips.length === 0 ? (
                  <span className="text-[9px] text-neutral-300">—</span>
                ) : (
                  chips.map(({ staff: s, status }) => {
                    const days = consecutiveDaysMap[s.id] ?? 0;
                    // Colour-coded chips: available uses orange tint; assigned = grey; leave = yellow
                    const chipColor =
                      status === 'leave'
                        ? 'bg-yellow-200'
                        : status === 'assigned'
                          ? 'bg-[#8c8c8c]'
                          : days >= 13
                            ? 'bg-red-500'
                            : days >= 10
                              ? 'bg-amber-500'
                              : 'bg-[#F26522]/20';
                    const chipTextColor =
                      status === 'leave'
                        ? 'text-neutral-900'
                        : status === 'assigned'
                          ? 'text-white/90'
                          : status === 'available' && days < 10
                            ? 'text-neutral-900'
                            : status === 'available'
                              ? 'text-white'
                              : 'text-white/90';
                    // "initials·days" when days > 0; initials only otherwise
                    const chipLabel =
                      days > 0 && status !== 'leave' ? `${s.initials}·${days}` : s.initials;
                    const isPill = days > 0 && status !== 'leave';
                    return (
                      <Tooltip key={s.id}>
                        <TooltipTrigger asChild>
                          <span
                            className={clsx(
                              'inline-flex items-center justify-center rounded-full font-medium cursor-default',
                              chipTextColor,
                              isPill ? 'px-1 h-[18px] text-[8px]' : 'w-[18px] h-[18px] text-[8px]',
                              chipColor,
                            )}
                          >
                            {chipLabel}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {s.known_as || s.name}
                          {status === 'leave'
                            ? ' — on leave'
                            : status === 'assigned'
                              ? ' — assigned'
                              : days > 0
                                ? ` — ${days} consecutive days`
                                : ''}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </TooltipProvider>

      {/* Jobs */}
      <div className="p-2 space-y-2">
        {rowCount === 0 ? (
          <div className="flex flex-col items-center justify-center text-neutral-300 py-8">
            <CalendarIcon className="w-5 h-5 mb-1" />
            <span className="text-[11px]">No jobs</span>
          </div>
        ) : (
          rowSlots.map((job, rowIndex) =>
            job ? (
              <JobCard
                key={`${job.id}-${day.toISOString()}`}
                job={job}
                staffMap={staffMap}
                dayPosition={getDayPosition(job, day)}
                dayNumber={getDayNumber(job, day)}
                cardDate={day}
                phaseCount={job.phaseCountTotal ?? 1}
                dragMeta={
                  canReschedule &&
                  !!job.phaseId &&
                  job.status !== 'cancelled' &&
                  job.status !== 'completed' &&
                  job.status !== 'postponed' &&
                  job.phaseStatus !== 'cancelled' &&
                  job.phaseStatus !== 'completed'
                    ? {
                        jobId: job.parentJobId ?? job.id,
                        phaseId: job.phaseId,
                        startDate: job.startDate,
                        durationDays: job.durationDays,
                        name: job.displayName,
                        ion: job.ion ?? null,
                      }
                    : null
                }
                vehicleRegistrations={vehiclesMap.get(`${job.id}:${format(day, 'yyyy-MM-dd')}`)}
                onClick={() => {
                  // Phase 39-05/07: parent owns the selection logic (snapshot
                  // lookup needs boardData). DayColumn just forwards the click.
                  onSelectJob(job, day, null);
                }}
                onOpenSection={(section) => {
                  // #102: same open as a card click, but seed the panel to expand
                  // the named section (vehicles / assigned staff) from the "+N" chip.
                  onSelectJob(job, day, section);
                }}
              />
            ) : (
              <div key={rowIndex} style={{ height: CARD_HEIGHT }} />
            ),
          )
        )}
      </div>
    </div>
  );
}

export function ContractsBoard() {
  // #217: phase rescheduling is gated on the contracts_board update permission;
  // viewers see no draggable cards (the board passes dragMeta={null}).
  const { can } = usePermissions();
  const canScheduleWork = can('contracts_board', 'add_scheduled_work');
  const canReschedule = can('contracts_board', 'update');
  // #217: one reschedule flow for the whole board — a drop on any day column
  // opens the same clash dialog the JobDetailPanel uses (confirm-first).
  const reschedule = useRescheduleFlow();
  const [mountedWeekStarts, setMountedWeekStarts] = useState<Date[]>(() =>
    buildWindow(new Date(), WINDOW_WEEKS),
  );
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobDate, setSelectedJobDate] = useState<Date | null>(null);
  // #102: when the panel is opened via a JobCard "+N" overflow chip, the section
  // to pre-expand (vehicles / assigned staff). Null for a normal card open.
  const [expandSection, setExpandSection] = useState<'vehicles' | 'staff' | null>(null);
  // Phase 39-05: phase context for the selected card. 39-06 JobDetailPanel
  // rebuild will consume this to scroll/focus the panel to the clicked phase.
  // 39-05 just threads the state through; no consumer reads it yet.

  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  // Phase 39-07 (closes DEFERRED-39-05-D): when a postponement-snapshot
  // tombstone is clicked, stash the snapshot so the panel can render the
  // "Snapshot from {date} by {name}" banner alongside the live parent job.
  const [selectedPostponement, setSelectedPostponement] = useState<
    import('../../lib/api').ApiBoardPostponement | null
  >(null);
  // #105: a postponed job hard-deletes all its phases (postponeJob), so it is
  // EXCLUDED from useJobs() (the /jobs query INNER JOINs job_phases[1]). Without
  // a fallback the panel never opens for a postponed tombstone. Stash the clicked
  // synthetic tombstone Job (adaptPostponement — status:'postponed') so
  // selectedJob can fall back to it when the live lookup misses.
  const [selectedTombstoneJob, setSelectedTombstoneJob] = useState<
    import('../data/mockData').Job | null
  >(null);
  const [showAvailableWork, setShowAvailableWork] = useState(false);
  const [schedulingWorkItem, setSchedulingWorkItem] = useState<ApiWorkItem | null>(null);
  // #100: when the user opens Available Work via a per-day calendar "+" button,
  // remember that day (yyyy-MM-dd) so the ScheduleModal seeds its start date
  // from it rather than today. Cleared when Available Work is opened via the
  // normal toolbar toggle, and when the modal closes.
  const [prefillStartDate, setPrefillStartDate] = useState<string | null>(null);
  useEffect(() => {
    if (!canScheduleWork) {
      setSchedulingWorkItem(null);
      setPrefillStartDate(null);
    }
  }, [canScheduleWork]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState('');
  const [visibleDateLabel, setVisibleDateLabel] = useState('');
  const [filters, setFilters] = useState<BoardFilters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  // Issue #98: surface that the board is filtered even when the filter panel is
  // collapsed (otherwise an empty/partial board looks like there's just no work).
  const hasActiveFilter = !!(
    filters.staffId ||
    filters.client ||
    filters.status ||
    filters.type ||
    filters.vehicleRegistrations.length > 0 ||
    filters.searchTerm
  );
  const [showDeals, setShowDeals] = useState(true);
  const [showTickets, setShowTickets] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const leftSentinelRef = useRef<HTMLDivElement>(null);
  const rightSentinelRef = useRef<HTMLDivElement>(null);
  // Direction of a pending sentinel-triggered slide, consumed by the layout effect
  // to compensate scrollLeft so the viewport doesn't jump. null when no slide pending.
  const pendingSlideRef = useRef<null | 'left' | 'right'>(null);
  // Target date for a pending jump (Today / date picker); the layout effect scrolls
  // that column into view after the window re-seeds. Seeded to today for first mount.
  const pendingScrollDateRef = useRef<Date | null>(new Date());

  const queryClient = useQueryClient();

  // Fetch jobs (with assignments embedded) and staff for passing to child components
  const { data: jobs = [], isLoading: jobsLoading } = useJobs();
  // #105: fall back to the clicked postponed tombstone when the live job lookup
  // misses (postponed jobs have no phases → excluded from useJobs()).
  const selectedJob = selectedJobId
    ? (jobs.find((j) => j.id === selectedJobId) ?? selectedTombstoneJob)
    : null;
  // Phase 39-05 follow-up #2 (2026-05-22): minimal phase indicator on the
  // panel. Lookup the clicked board-row by phase_id; pass its phase context
  // (number / status / total) to JobDetailPanel as an interim until 39-06
  // restructures the panel around full phase awareness.
  const { data: staffList = [] } = useStaff();
  // Phase 37-01 (Issue #54): vehicles list feeds the BoardFilterBar multi-select.
  const { data: vehicleList = [] } = useVehicles();
  // Fetch board data for loaded weeks
  const {
    data: boardData,
    loadingWeeks,
    hasPartialError,
    dataUpdatedAt,
    weekStarts,
  } = useBoardWeeks(mountedWeekStarts);

  // Always fetch available work items so the badge count is correct.
  const { data: availableWorkItems = [], isLoading: workItemsLoading } = useWorkItems({
    status: 'available',
  });

  // Build flat list of visible days from loaded weeks
  const visibleDays = useMemo(() => {
    if (weekStarts.length === 0) return [];
    return eachDayOfInterval({
      start: weekStarts[0],
      end: addDays(weekStarts[weekStarts.length - 1], 6),
    });
  }, [weekStarts]);

  // Per-day consecutive-days map — keyed by 'YYYY-MM-DD' then staffId → count
  const consecutiveDaysRangeFrom = visibleDays.length > 0 ? visibleDays[0] : new Date();
  const consecutiveDaysRangeTo =
    visibleDays.length > 0 ? visibleDays[visibleDays.length - 1] : new Date();
  const { data: consecutiveDaysByDate = {} } = useConsecutiveDaysByDate(
    consecutiveDaysRangeFrom,
    consecutiveDaysRangeTo,
  );

  // Phase 30-01 + Phase 39-05: per-status visual treatment (cancelled /
  // completed / postponed → tombstone) handled in JobCard. Postponed jobs
  // are also returned to Available Work in parallel — tombstone preserves
  // the planner's "what was scheduled here" audit context.
  //
  // Phase 39-05: board layout now derives from boardData.jobs[] (post-39-04
  // per-(phase, date) projection) instead of useJobs() (per-job). Each board
  // row becomes a Job with `id = phase_id` so multi-phase jobs surface as N
  // distinct cards (one per active phase). `parentJobId` carries the actual
  // job UUID for cross-references. useJobs() is retained for JobDetailPanel
  // lookup (selectedJob via `jobs.find(j => j.id === selectedJobId)` —
  // 39-06 rebuilds the panel to consume phase context properly).
  const unfilteredActiveJobs = useMemo(
    () => [
      // Phase 40-01 fixup (Tom UAT 2026-05-26): pass boardData.segments to
      // adaptBoardPhaseRow so Job.assignedStaff is populated for board cards.
      // Pre-fix this was always [] — the day-header staff chip strip + any
      // JobCard staff badge derivation always showed staff as "available"
      // even when assigned. With segments threaded through here AND the
      // resource mutations invalidating ['board'] (committed earlier in
      // 566160c), staff assignments now reflect on the calendar without a
      // page refresh.
      ...boardData.jobs.map((row) => adaptBoardPhaseRow(row, boardData.segments)),
      // Phase 39-05 follow-up (2026-05-25, Migration 065): postponement
      // snapshots fed into the same layout pipeline as live phases.
      // Each snapshot adapts into a synthetic Job with status='postponed',
      // unique id (snapshot.id), and the captured phase's date range —
      // JobCard's parent-status tombstone branch renders "POSTPONED".
      ...boardData.postponements.map((p) => adaptPostponement(p)),
    ],
    [boardData.jobs, boardData.segments, boardData.postponements],
  );
  // Staff name map for search (id → display name)
  const staffNameMap = useMemo(
    () => new Map(boardData.staff.map((s) => [s.id, s.known_as || s.name])),
    [boardData.staff],
  );

  // Phase 37-01 (Issue #54): aggregate per-job registrations across all
  // displayed dates for the vehicle filter intersection. Keyed by jobId →
  // Set<registration>. Built once from boardData.vehicles.
  const vehicleRegsByJobId = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const v of boardData.vehicles) {
      // Issue #96 fix: phased board cards override Job.id to phase_id (adapters
      // toPhaseJob), but vehicles carry both job_id + phase_id. Key by phase_id
      // (falling back to job_id for non-phased) so the filter lookup — which is
      // by the card's Job.id — actually matches. Was keyed by v.job_id only, so
      // the vehicle filter returned nothing for every phased job.
      const key = v.phase_id ?? v.job_id;
      let set = m.get(key);
      if (!set) {
        set = new Set();
        m.set(key, set);
      }
      for (const r of v.registrations) set.add(r);
    }
    return m;
  }, [boardData.vehicles]);

  const activeJobs = useMemo(
    () => applyBoardFilters(unfilteredActiveJobs, filters, staffNameMap, vehicleRegsByJobId),
    [unfilteredActiveJobs, filters, staffNameMap, vehicleRegsByJobId],
  );

  // Compute row assignments once for all active jobs so multi-day jobs
  // appear in the same lane across every day column they span
  const { rowMap, rowCount } = useMemo(() => assignJobRows(activeJobs), [activeJobs]);

  // Build a map for O(1) staff lookup in child components
  const staffMap = new Map<string, Staff>(staffList.map((s) => [s.id, s]));

  // Phase 37-01 (Issue #51) + Phase 39-05: per-(phase, date) vehicle
  // registration lookup for the JobCard mid-section pills. Re-keyed in
  // 39-05 from `${job_id}:${date}` to `${phase_id}:${date}` so multi-phase
  // jobs with distinct vehicles per phase resolve correctly. Single-phase
  // legacy data still resolves naturally — each job_phases row has its own
  // phase_id and the backend GROUP BY produces one row per (phase, date).
  const vehiclesMap = useMemo(
    () => new Map(boardData.vehicles.map((v) => [`${v.phase_id}:${v.date}`, v.registrations])),
    [boardData.vehicles],
  );

  // Phase 39-05 Spec [2] amendment (2026-05-22): phase badge denominator now
  // comes from `job.phaseCountTotal` projected server-side as the count of
  // ALL phases on the parent job (any status, regardless of window). Stable
  // per-job — the May Phase 1 card and the August Phase 5 card both read
  // "of 5" for a job with 5 phases. Cancelled phases are counted (planner
  // signal). Replaced the earlier window-scoped `phaseCountByJobId` Map
  // which only saw phases inside the loaded weeks and mis-numbered cards.

  // ── Keep the viewport stable across window changes ───────────────────────────
  // Runs after every mountedWeekStarts change. Two jobs:
  //  (1) sentinel slide → compensate scrollLeft by one week so content stays put.
  //  (2) Today / date-picker jump → scroll the target date's column into view.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const dir = pendingSlideRef.current;
    if (dir) {
      container.scrollLeft += scrollDeltaForSlide(dir, WEEK_PX);
      pendingSlideRef.current = null;
    }

    const target = pendingScrollDateRef.current;
    if (target) {
      const sel = `[data-date="${format(target, 'yyyy-MM-dd')}"]`;
      container
        // #236: block:'nearest' keeps the vertical position put (no scroll when the
        // target is already in view) so the sticky toolbar/day headers aren't hidden;
        // we only want the horizontal inline-centering on the target date column.
        .querySelector<HTMLElement>(sel)
        ?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
      pendingScrollDateRef.current = null;
    }
  }, [mountedWeekStarts]);

  // ── IntersectionObserver for infinite lazy loading ───────────────────────────
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Guard against double-firing while a slide is IN FLIGHT (dispatched but
        // not yet compensated). pendingSlideRef is set here and cleared by the
        // layout effect once scrollLeft is corrected and the sentinel has been
        // pushed back out of view. This replaces an earlier fixed 500ms time
        // cooldown which, combined with the edge-triggered observer, swallowed any
        // re-intersection that landed inside the window — stalling continuous
        // (e.g. trackpad) scrolling at every week boundary until the user nudged
        // back-and-forth. A condition guard only suppresses the ~1-frame in-flight
        // window, so the next genuine re-entry slides immediately.
        if (pendingSlideRef.current) return;

        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          if (entry.target === leftSentinelRef.current) {
            pendingSlideRef.current = 'left';
            setMountedWeekStarts((weeks) => slideLeft(weeks));
            break;
          } else if (entry.target === rightSentinelRef.current) {
            pendingSlideRef.current = 'right';
            setMountedWeekStarts((weeks) => slideRight(weeks));
            break;
          }
        }
      },
      { root: container, rootMargin: '0px 200px 0px 200px', threshold: 0 },
    );

    if (leftSentinelRef.current) observer.observe(leftSentinelRef.current);
    if (rightSentinelRef.current) observer.observe(rightSentinelRef.current);

    return () => observer.disconnect();
  }, []);

  // ── Update visible date label on scroll ──────────────────────────────────────
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Find the day column closest to the scroll center using element positions
      const scrollCenter = container.scrollLeft + container.clientWidth / 2;
      const cols = container.querySelectorAll<HTMLElement>('[data-day-col]');
      let closestIdx = 0;
      let closestDist = Infinity;
      cols.forEach((col, i) => {
        const colCenter = col.offsetLeft + col.offsetWidth / 2;
        const dist = Math.abs(colCenter - scrollCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      const day = visibleDays[Math.min(closestIdx, visibleDays.length - 1)];
      if (day) {
        const ws = startOfWeek(day, { weekStartsOn: 1 });
        const we = addDays(ws, 6);
        setVisibleDateLabel(`${format(ws, 'MMM d')} - ${format(we, 'MMM d, yyyy')}`);
      }
    };

    handleScroll(); // set initial
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [visibleDays]);

  // ── Keyboard navigation ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!scrollRef.current) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const colWidth = scrollRef.current.querySelector('[data-day-col]')?.clientWidth ?? COL_WIDTH;
      if (e.key === 'ArrowLeft') {
        scrollRef.current.scrollBy({ left: -colWidth, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight') {
        scrollRef.current.scrollBy({ left: colWidth, behavior: 'smooth' });
      } else if (e.key === 'Home') {
        pendingScrollDateRef.current = new Date();
        setMountedWeekStarts(buildWindow(new Date(), WINDOW_WEEKS));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for job selection from the header NotificationBell (custom DOM event)
  useEffect(() => {
    const handleSelectJob = (e: Event) => {
      const jobId = (e as CustomEvent<{ jobId: string }>).detail.jobId;
      if (jobId) setSelectedJobId(jobId);
    };
    window.addEventListener('select-job', handleSelectJob);
    return () => window.removeEventListener('select-job', handleSelectJob);
  }, []);

  const scrollToToday = useCallback(() => {
    pendingScrollDateRef.current = new Date();
    setMountedWeekStarts(buildWindow(new Date(), WINDOW_WEEKS));
  }, []);

  const jumpToDate = useCallback((dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return;
    pendingScrollDateRef.current = date;
    setMountedWeekStarts(buildWindow(date, WINDOW_WEEKS));
    setShowDatePicker(false);
    setDatePickerValue('');
  }, []);

  // Card click / "+N" overflow open. Lifted out of the day-column JSX (Phase
  // 39-05/07): the snapshot lookup needs boardData, so it stays at board level
  // and DayColumn forwards the clicked job + day + which section to expand.
  const openJobFromCard = useCallback(
    (job: Job, day: Date, section: 'vehicles' | 'staff' | null) => {
      // Phase 39-07 (closes DEFERRED-39-05-D): if this click came from a
      // postponement-snapshot tombstone (status='postponed' + adaptPostponement-
      // shaped — its id matches a row in boardData.postponements), also stash the
      // snapshot details so the panel can show the "historical postponement" banner.
      // Edge case (parent job since deleted; rare pre-launch) no-ops silently —
      // useJobs().find returns undefined, panel doesn't open.
      const snapshot = boardData.postponements.find((p) => p.id === job.id);
      setSelectedJobId(job.parentJobId ?? job.id);
      setSelectedJobDate(day);
      setSelectedPhaseId(job.phaseId ?? null);
      setSelectedPostponement(snapshot ?? null);
      // #105: postponed tombstone → keep the synthetic Job as a fallback so the
      // (phase-less) panel still opens. Re-key it to the LIVE parent job id
      // (original_job_id) — the panel's sub-resource queries hit /jobs/:id which
      // 404s on an unknown id; the parent job still exists (postponed, zero phases
      // — allocations cascade-deleted), so it returns clean empty lists.
      setSelectedTombstoneJob(snapshot ? { ...job, id: job.parentJobId ?? job.id } : null);
      setExpandSection(section);
    },
    [boardData.postponements],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col">
        {/* #217: custom multi-day drag ghost — one provider wraps the whole
            board tree (drag sources + the layer). */}
        <PhaseDragLayer />
        <DragAutoScroller scrollRef={scrollRef} />
        {/* #217: a day-column drop opens the same clash dialog the panel uses. */}
        {reschedule.dialog && (
          <ReassignmentDialog
            state={reschedule.dialog}
            fromLabel={reschedule.dialog.fromLabel ?? ''}
            isPhase={reschedule.dialog.isPhase}
            isExecuting={reschedule.isExecuting}
            onTransfer={reschedule.onTransfer}
            onClear={reschedule.onClear}
            onCancel={reschedule.onCancel}
            onDone={reschedule.onDone}
          />
        )}
        {/* Sticky header + filters */}
        <div className="sticky top-0 z-10 bg-white">
          {/* Board Header */}
          <div className="shrink-0 px-4 sm:px-6 py-3 border-b border-neutral-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center rounded-md border border-neutral-200 bg-white shadow-sm overflow-hidden">
                <button
                  onClick={() =>
                    scrollRef.current?.scrollBy({ left: -WEEK_PX, behavior: 'smooth' })
                  }
                  className="p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-[#F26522] transition-colors"
                  aria-label="Previous week"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="px-3 py-1 text-[13px] text-neutral-800 border-x border-neutral-200 min-w-[170px] text-center">
                  {visibleDateLabel || 'Loading…'}
                </div>
                <button
                  onClick={() => scrollRef.current?.scrollBy({ left: WEEK_PX, behavior: 'smooth' })}
                  className="p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-[#F26522] transition-colors"
                  aria-label="Next week"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={scrollToToday}
                className="px-3 py-1.5 text-[12px] text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 shadow-sm transition-colors"
              >
                Today
              </button>
              {/* Jump to date */}
              <div className="relative">
                <button
                  onClick={() => setShowDatePicker((v) => !v)}
                  className="p-1.5 text-neutral-500 hover:bg-neutral-100 rounded-md transition-colors"
                  aria-label="Jump to date"
                >
                  <CalendarIcon className="w-4 h-4" />
                </button>
                {showDatePicker && (
                  <div className="absolute top-full mt-1 left-0 bg-white border border-neutral-200 rounded-lg shadow-lg p-3 z-20">
                    <input
                      type="date"
                      value={datePickerValue}
                      onChange={(e) => setDatePickerValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && datePickerValue) jumpToDate(datePickerValue);
                        if (e.key === 'Escape') setShowDatePicker(false);
                      }}
                      className="text-[13px] border border-neutral-200 rounded px-2 py-1"
                      autoFocus
                    />
                    <button
                      onClick={() => datePickerValue && jumpToDate(datePickerValue)}
                      className="ml-2 px-2 py-1 text-[12px] bg-[#F26522] text-white rounded hover:bg-[#d9561d] transition-colors"
                    >
                      Go
                    </button>
                  </div>
                )}
              </div>
              {/* Last refresh indicator */}
              {dataUpdatedAt > 0 && (
                <span className="hidden sm:flex items-center gap-1 text-[11px] text-neutral-400">
                  <RefreshCw className="w-3 h-3" />
                  {format(new Date(dataUpdatedAt), 'HH:mm')}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Issue #98: "Results are being filtered" notice when any filter is
                active — shown whether or not the filter panel is expanded. */}
              {hasActiveFilter && (
                <span className="text-[12px] font-medium text-[#F26522] whitespace-nowrap">
                  Results are being filtered
                </span>
              )}
              {/* Filters toggle */}
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] transition-colors shadow-sm',
                  showFilters
                    ? 'bg-[#F26522]/10 border-[#F26522]/30 text-[#F26522]'
                    : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50',
                )}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Filters
              </button>
              {/* Available Work toggle */}
              <button
                onClick={() => {
                  // #100: opening via the toolbar is a generic open — clear any
                  // day-seeded prefill so the ScheduleModal defaults to today.
                  setPrefillStartDate(null);
                  setShowAvailableWork((v) => !v);
                }}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-[12px] transition-colors shadow-sm',
                  showAvailableWork
                    ? 'bg-[#F26522]/10 border-[#F26522]/30 text-[#F26522]'
                    : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50',
                )}
              >
                <Inbox className="w-3.5 h-3.5" />
                Available Work
                {availableWorkItems.length > 0 && (
                  <span
                    className={clsx(
                      'ml-0.5 px-1.5 py-0.5 rounded-full text-[10px]',
                      showAvailableWork
                        ? 'bg-[#F26522] text-white'
                        : 'bg-neutral-200 text-neutral-600',
                    )}
                  >
                    {availableWorkItems.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Board Filters (collapsible) */}
          {showFilters && (
            <div className="border-b border-neutral-200">
              <BoardFilterBar
                jobs={unfilteredActiveJobs}
                staffList={boardData.staff}
                vehicleList={vehicleList}
                filters={filters}
                onFiltersChange={setFilters}
              />
            </div>
          )}
        </div>

        {/* Loading overlay — jobs only; board data loads in background */}
        {jobsLoading && (
          <div className="shrink-0 px-4 py-2 bg-amber-50 border-b border-amber-100 text-[12px] text-amber-700 text-center">
            Loading jobs…
          </div>
        )}

        {/* Partial error banner */}
        {hasPartialError && (
          <div className="shrink-0 px-4 py-2 bg-amber-50 border-b border-amber-100 text-[12px] text-amber-700 text-center flex items-center justify-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            Some availability data could not be loaded.
            <button
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['board', 'week'] });
              }}
              className="underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex">
          {/* Available Work Panel */}
          {showAvailableWork && (
            <div className="w-72 shrink-0 border-r border-neutral-200 bg-neutral-50 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                <span className="text-[12px] text-neutral-500 uppercase tracking-wide">
                  Available Work
                </span>
                <button
                  onClick={() => setShowAvailableWork(false)}
                  className="p-1 rounded hover:bg-neutral-200 text-neutral-400 transition-colors"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {workItemsLoading && (
                  <p className="text-[12px] text-neutral-400 italic text-center py-4">Loading…</p>
                )}
                {!workItemsLoading && availableWorkItems.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-neutral-300">
                    <Inbox className="w-6 h-6 mb-2" />
                    <span className="text-[11px]">No available work items</span>
                  </div>
                )}
                {(() => {
                  const deals = availableWorkItems.filter((wi) => wi.source !== 'hubspot_ticket');
                  const tickets = availableWorkItems.filter((wi) => wi.source === 'hubspot_ticket');

                  const renderCard = (wi: ApiWorkItem) => {
                    const isTicket = wi.source === 'hubspot_ticket';
                    const displayId = wi.ion ?? (isTicket ? `T-${wi.source_id}` : null);
                    const canSchedule = isTicket || !!wi.ion;
                    return (
                      <div
                        key={wi.id}
                        className="bg-white border border-neutral-200 rounded-lg p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-[10px] font-mono text-neutral-400">
                            {displayId ?? 'No ION'}
                          </span>
                          <div className="flex gap-1">
                            {isTicket ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-100 shrink-0">
                                Ticket
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 shrink-0">
                                Deal
                              </span>
                            )}
                            {!displayId && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 shrink-0">
                                ION pending
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-[13px] text-neutral-900 leading-snug line-clamp-2 mb-1.5">
                          {wi.name}
                        </p>
                        {wi.client_name && (
                          <p className="text-[11px] text-neutral-500 mb-1">{wi.client_name}</p>
                        )}
                        {wi.site_address && (
                          <div className="flex items-center gap-1 text-[11px] text-neutral-400 mb-2">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{wi.site_address}</span>
                          </div>
                        )}
                        {wi.source_metadata?.suggested_start_date && (
                          <p className="text-[11px] text-neutral-400 mb-2">
                            Suggested: {wi.source_metadata.suggested_start_date}
                          </p>
                        )}
                        {canScheduleWork && (
                          <button
                            disabled={!canSchedule}
                            title={!canSchedule ? 'ION required before scheduling' : undefined}
                            onClick={() =>
                              canScheduleWork && canSchedule && setSchedulingWorkItem(wi)
                            }
                            className="w-full mt-1 py-1.5 rounded-md text-[12px] bg-[#F26522] text-white hover:bg-[#d9561d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Schedule
                          </button>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {deals.length > 0 && (
                        <div>
                          <button
                            onClick={() => setShowDeals((v) => !v)}
                            className="flex items-center gap-1.5 w-full px-1 py-1.5 text-[11px] font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
                          >
                            <ChevronDown
                              className={clsx(
                                'w-3.5 h-3.5 transition-transform',
                                !showDeals && '-rotate-90',
                              )}
                            />
                            Deals
                            <span className="ml-auto text-[10px] bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded-full">
                              {deals.length}
                            </span>
                          </button>
                          {showDeals && (
                            <div className="space-y-2 mt-1">{deals.map(renderCard)}</div>
                          )}
                        </div>
                      )}
                      {tickets.length > 0 && (
                        <div>
                          <button
                            onClick={() => setShowTickets((v) => !v)}
                            className="flex items-center gap-1.5 w-full px-1 py-1.5 text-[11px] font-medium text-neutral-600 hover:text-neutral-800 transition-colors"
                          >
                            <ChevronDown
                              className={clsx(
                                'w-3.5 h-3.5 transition-transform',
                                !showTickets && '-rotate-90',
                              )}
                            />
                            Tickets
                            <span className="ml-auto text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-100 px-1.5 py-0.5 rounded-full">
                              {tickets.length}
                            </span>
                          </button>
                          {showTickets && (
                            <div className="space-y-2 mt-1">{tickets.map(renderCard)}</div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Horizontal Scrolling Calendar Grid */}
          <div ref={scrollRef} className="flex-1 overflow-x-auto">
            <div className="flex p-4 gap-3">
              {/* Left sentinel for lazy loading */}
              <div ref={leftSentinelRef} className="w-px shrink-0" />

              {visibleDays.map((day) => {
                // isWeekend / bank-holiday / today + the column chrome are derived
                // inside DayColumn now; the map only assembles the per-day data.
                const dayStaff = getAllStaffByDay(
                  day,
                  boardData.staff,
                  boardData.leave,
                  activeJobs,
                );
                const dayJobs = getJobsForDay(activeJobs, day);
                // Build a fixed grid of row slots for this day using the global row assignment
                const rowSlots = Array.from(
                  { length: rowCount },
                  (_, rowIndex) => dayJobs.find((j) => rowMap.get(j.id) === rowIndex) ?? null,
                );
                const isWeekLoading = loadingWeeks.has(
                  format(startOfWeek(day, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
                );
                const consecutiveDaysMap = consecutiveDaysByDate[format(day, 'yyyy-MM-dd')] ?? {};

                return (
                  <DayColumn
                    key={day.toISOString()}
                    day={day}
                    dayStaff={dayStaff}
                    rowSlots={rowSlots}
                    rowCount={rowCount}
                    isWeekLoading={isWeekLoading}
                    consecutiveDaysMap={consecutiveDaysMap}
                    staffMap={staffMap}
                    vehiclesMap={vehiclesMap}
                    canScheduleWork={canScheduleWork}
                    canReschedule={canReschedule}
                    reschedule={reschedule}
                    onAddJob={(dayStr) => {
                      if (!canScheduleWork) return;
                      setPrefillStartDate(dayStr);
                      setShowAvailableWork(true);
                    }}
                    onSelectJob={openJobFromCard}
                  />
                );
              })}

              {/* Right sentinel for lazy loading */}
              <div ref={rightSentinelRef} className="w-px shrink-0" />
            </div>
          </div>
        </div>

        {/* Job Detail Panel */}
        {selectedJob && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setSelectedJobId(null)}
            />
            <JobDetailPanel
              job={selectedJob}
              staffMap={staffMap}
              selectedDate={selectedJobDate ?? undefined}
              expandSection={expandSection}
              phaseContext={(() => {
                if (!selectedPhaseId) return null;
                const row = boardData.jobs.find((j) => j.phase_id === selectedPhaseId);
                if (!row || row.phase_number == null) return null;
                return {
                  // Phase 39-07: panel navigator defaults to this phaseId.
                  phaseId: selectedPhaseId,
                  phaseNumber: row.phase_number,
                  phaseStatus: row.phase_status ?? null,
                  phaseCountTotal: row.phase_count_total ?? 1,
                };
              })()}
              postponementContext={
                selectedPostponement
                  ? {
                      postponedAt: selectedPostponement.postponed_at,
                      postponedBy: selectedPostponement.postponed_by,
                      postponedReason: selectedPostponement.postponed_reason,
                      phaseNumber: selectedPostponement.phase_number,
                    }
                  : null
              }
              onClose={() => {
                setSelectedJobId(null);
                setSelectedJobDate(null);
                setSelectedPostponement(null);
                setSelectedTombstoneJob(null);
              }}
              onTransitioned={() => {
                setSelectedJobId(null);
                setSelectedJobDate(null);
                setSelectedPostponement(null);
                setSelectedTombstoneJob(null);
              }}
              onEdited={() => {
                setSelectedJobId(null);
                setSelectedJobDate(null);
                setSelectedPostponement(null);
                setSelectedTombstoneJob(null);
              }}
            />
          </>
        )}

        {/* Schedule Modal */}
        {canScheduleWork && schedulingWorkItem && (
          <ScheduleModal
            workItem={schedulingWorkItem}
            initialStartDate={prefillStartDate ?? undefined}
            onClose={() => {
              setSchedulingWorkItem(null);
              // #100: consume the day-seed once the modal closes.
              setPrefillStartDate(null);
            }}
          />
        )}
      </div>
    </DndProvider>
  );
}
